/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { withMyTheme } from '../theme/theme';
import { mobileCss } from '../theme/isMobile';
import { useState, useRef, useEffect, ChangeEvent } from 'react';
import { MyButton } from '../components/button/MyButton';
import { CircularProgress, IconButton } from '@mui/material';
import { Offer, OfferImage } from './OffersList';
import { getImageFromGit } from './getImageFromGit';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import DeleteIcon from '@mui/icons-material/Delete';

interface OfferEditFormProps {
  offer: Offer;
  onSave: (updatedOffer: Offer, mainImage?: File, desktopImage?: File, additionalImageFiles?: File[]) => Promise<void>;
  onCancel: () => void;
}

const FormContainerStyle = withMyTheme((theme) => css`
  background-color: ${theme.palette.background.paper};
  padding: 2rem;
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
`);

const FormTitleStyle = withMyTheme((theme) => css`
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
  color: ${theme.palette.primary.main};
  font-family: ${theme.typography.h1.fontFamily};
`);

const FormGroupStyle = css`
  margin-bottom: 1.5rem;
`;

const LabelStyle = withMyTheme((theme) => css`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: ${theme.palette.text.primary};
  font-family: ${theme.typography.body1.fontFamily};
`);

const InputStyle = withMyTheme((theme) => css`
  width: 100%;
  padding: 0.75rem;
  font-size: 1rem;
  border: 1px solid ${theme.palette.divider};
  border-radius: 4px;
  background-color: ${theme.palette.background.default};
  color: ${theme.palette.text.primary};
  font-family: ${theme.typography.body1.fontFamily};
  
  &:focus {
    outline: none;
    border-color: ${theme.palette.primary.main};
    box-shadow: 0 0 0 2px ${theme.palette.primary.main}40;
  }
`);

const TextareaStyle = withMyTheme((theme) => css`
  ${InputStyle(theme)}
  min-height: 200px;
  resize: vertical;
`);

const ButtonContainerStyle = css`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 2rem;
  
  ${mobileCss(`
    flex-direction: column;
  `)}
`;

const ImagePreviewContainerStyle = css`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
  flex-wrap: wrap;
`;

const ImagePreviewStyle = withMyTheme((theme) => css`
  width: 150px;
  height: 150px;
  object-fit: cover;
  border-radius: 4px;
  border: 1px solid ${theme.palette.divider};
`);

const ImageUploadContainerStyle = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
`;

const FileInputLabelStyle = withMyTheme((theme) => css`
  display: inline-block;
  padding: 0.5rem 1rem;
  background-color: ${theme.palette.primary.main};
  color: ${theme.palette.primary.contrastText};
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: ${theme.palette.primary.dark};
  }
`);

const FileInputStyle = css`
  display: none;
`;

const ImageCaptionStyle = withMyTheme((theme) => css`
  font-size: 0.8rem;
  color: ${theme.palette.text.secondary};
  text-align: center;
  margin-top: 0.25rem;
`);

const AdditionalImagesContainerStyle = css`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-top: 1rem;
`;

const AdditionalImageItemStyle = withMyTheme((theme) => css`
  position: relative;
  width: 150px;
  margin-bottom: 1rem;
`);

const DeleteImageButtonStyle = withMyTheme((theme) => css`
  position: absolute;
  top: -10px;
  right: -10px;
  background-color: ${theme.palette.error.main};
  color: white;
  width: 30px;
  height: 30px;
  padding: 5px;
  
  &:hover {
    background-color: ${theme.palette.error.dark};
  }
`);

const AddImageButtonStyle = withMyTheme((theme) => css`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 150px;
  height: 150px;
  border: 2px dashed ${theme.palette.divider};
  border-radius: 4px;
  cursor: pointer;
  transition: border-color 0.2s;
  
  &:hover {
    border-color: ${theme.palette.primary.main};
  }
`);

export const OfferEditForm = ({ offer, onSave, onCancel }: OfferEditFormProps) => {
  const [formData, setFormData] = useState<Offer>({ ...offer });
  const [mainImageFile, setMainImageFile] = useState<File | null>(null);
  const [desktopImageFile, setDesktopImageFile] = useState<File | null>(null);
  const [mainImagePreview, setMainImagePreview] = useState<string | null>(null);
  const [desktopImagePreview, setDesktopImagePreview] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Additional images state
  const [additionalImageFiles, setAdditionalImageFiles] = useState<(File | null)[]>([]);
  const [additionalImagePreviews, setAdditionalImagePreviews] = useState<(string | null)[]>([]);
  const [additionalImageUrls, setAdditionalImageUrls] = useState<string[]>([]);

  const mainImageInputRef = useRef<HTMLInputElement>(null);
  const desktopImageInputRef = useRef<HTMLInputElement>(null);
  const additionalImageInputRef = useRef<HTMLInputElement>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleMainImageChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setMainImageFile(file);

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setMainImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDesktopImageChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setDesktopImageFile(file);

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setDesktopImagePreview(reader.result as any);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAdditionalImageChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setAdditionalImageFiles(prev => [...prev, file]);
        setAdditionalImagePreviews(prev => [...prev, reader.result as string]);
      };
      reader.readAsDataURL(file);

      // Reset the input so the same file can be selected again
      if (additionalImageInputRef.current) {
        additionalImageInputRef.current.value = '';
      }
    }
  };

  const handleRemoveAdditionalImage = (index: number) => {
    setAdditionalImageFiles(prev => prev.filter((_, i) => i !== index));
    setAdditionalImagePreviews(prev => prev.filter((_, i) => i !== index));
    setAdditionalImageUrls(prev => prev.filter((_, i) => i !== index));
    // @ts-ignore
    setFormData(prev => ({ ...prev, additional_images: prev.additional_images.filter((_, i) => i !== index) }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);

    try {
      // Create a copy of the form data with updated images array
      const updatedFormData = { ...formData };
      // Add additional image files to the form data
      // @ts-ignore
      const additionalFiles = (additionalImageFiles.filter(file => file !== null) as File[])

      await onSave(
        updatedFormData,
        mainImageFile || undefined,
        desktopImageFile || undefined,
        additionalFiles || undefined,
      );
    } catch (error) {
      console.error('Error saving offer:', error);
      alert('Nie udało się zapisać oferty. Spróbuj ponownie.');
    } finally {
      setIsSaving(false);
    }
  };

  // Function to get the image blob with authentication
  const [imageUrls, setImageUrls] = useState<Record<string, string>>({});

  useEffect(() => {
    // Fetch main image if available
    if (offer.main_image && offer.main_image?.url) {
      fetchImageWithAuth(offer.main_image.url, 'main');
    }

    // Fetch desktop image if available
    if (offer.image_desktop?.url) {
      fetchImageWithAuth(offer.image_desktop.url, 'desktop');
    }

    // Fetch additional images (skip the first one as it's the main image)
    if (offer.main_image) {
      const additionalImages = offer.additional_images || [];
      Promise.all(additionalImages.map(img => getImageFromGit(img.url)))
        .then(urls => {
          setAdditionalImageUrls(urls);
        });
    }
  }, [offer]);

  const fetchImageWithAuth = async (imagePath: string, imageType: string) => {
    const url = await getImageFromGit(imagePath)
    setImageUrls(prev => ({ ...prev, [imageType]: url }));
  };

  return (
    <form css={FormContainerStyle} onSubmit={handleSubmit}>
      <h2 css={FormTitleStyle}>Edycja oferty: {offer.name}</h2>

      <div css={FormGroupStyle}>
        <label css={LabelStyle} htmlFor="name">Nazwa (krótka na stronę główną)</label>
        <input
          css={InputStyle}
          type="text"
          id="name"
          name="name"
          value={formData.name}
          onChange={handleInputChange}
          required
        />
      </div>

      <div css={FormGroupStyle}>
        <label css={LabelStyle} htmlFor="details_name">Nazwa wyswietlana w szczegółach (opcjonalna, pozostaw puste jeśli taka sama jak Nazwa na stronie głównej)</label>
        <input
          css={InputStyle}
          type="text"
          id="details_name"
          name="details_name"
          value={formData.details_name}
          onChange={handleInputChange}
        />
      </div>

      <div css={FormGroupStyle}>
        <label css={LabelStyle} htmlFor="slug">Slug (końcówka w URL np. wpisanie <i>kurs-dla-wizazystow</i> oznacza peły link: <i>https://agatasawickamakeup.pl/oferta/kurs-dla-wizazystow</i>)</label>
        <input
          css={InputStyle}
          type="text"
          id="slug"
          name="slug"
          value={formData.slug}
          onChange={handleInputChange}
          required
        />
      </div>

      <div css={FormGroupStyle}>
        <label css={LabelStyle} htmlFor="order">Kolejność wyświetlania</label>
        <input
          css={InputStyle}
          type="number"
          id="order"
          name="order"
          value={formData.order}
          onChange={handleInputChange}
          min="1"
          required
        />
      </div>

      <div css={FormGroupStyle}>
        <label css={LabelStyle} htmlFor="description">Opis (HTML)</label>
        <textarea
          css={TextareaStyle}
          id="description"
          name="description"
          value={formData.description}
          onChange={handleInputChange}
          required
        />
      </div>

      <div css={FormGroupStyle}>
        <label css={LabelStyle}>Zdjęcie na telefon (mobilne)</label>

        <div css={ImagePreviewContainerStyle}>
          {/* Main Image */}
          <div css={ImageUploadContainerStyle}>
            {!mainImagePreview && imageUrls.main && <img
              src={imageUrls.main}
              alt="Podgląd głównego zdjęcia"
              css={ImagePreviewStyle}
              onError={(e) => {
                (e.target as HTMLImageElement).src = '';
              }}
            />}
            {mainImagePreview && <img src={mainImagePreview} css={ImagePreviewStyle} />}
            {!mainImagePreview && !imageUrls.main && <AddImageButton inputRef={mainImageInputRef} onChange={handleMainImageChange} />}
            {!!imageUrls.main && <label css={FileInputLabelStyle} htmlFor="mainImage">
              {'Zmień zdjęcie'}
            </label>}
            <input
              css={FileInputStyle}
              type="file"
              id="mainImage"
              ref={mainImageInputRef}
              accept="image/*"
              onChange={handleMainImageChange}
            />
            <span css={ImageCaptionStyle}>
              {mainImageFile ? mainImageFile.name : 'Wersja mobilna'}
            </span>
          </div>
        </div>
      </div>

      {/* Additional Images */}
      <div css={FormGroupStyle}>
        <label css={LabelStyle}>Dodatkowe zdjęcia (opcjonalne - wyświetlane w szczegółach oferty)</label>
        <div css={AdditionalImagesContainerStyle}>
          {/* Existing additional images */}
          {additionalImageUrls.map((url, index) => (
            <div key={`existing-${index}`} css={AdditionalImageItemStyle}>
              <img src={url} alt={`Dodatkowe zdjęcie ${index + 1}`} css={ImagePreviewStyle} />
              <IconButton
                css={DeleteImageButtonStyle}
                onClick={() => handleRemoveAdditionalImage(index)}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
              <span css={ImageCaptionStyle}>Zdjęcie {index + 1}</span>
            </div>
          ))}

          {/* New additional images */}
          {additionalImagePreviews.map((preview, index) => (
            <div key={`new-${index}`} css={AdditionalImageItemStyle}>
              {preview && <img src={preview} alt={`Nowe dodatkowe zdjęcie ${index + 1}`} css={ImagePreviewStyle} />}
              <IconButton
                css={DeleteImageButtonStyle}
                onClick={() => handleRemoveAdditionalImage(index)}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
              <span css={ImageCaptionStyle}>
                {additionalImageFiles[index]?.name || `Nowe zdjęcie ${index + 1}`}
              </span>
            </div>
          ))}

          {/* Add image button */}
          <AddImageButton inputRef={additionalImageInputRef} onChange={handleAdditionalImageChange} />
        </div>
      </div>
      <div css={FormGroupStyle}>
        <label css={LabelStyle}>Zdjęcie na laptopa ("Desktop")</label>
        <div css={ImagePreviewContainerStyle}>
          {/* Main Image */}
          <div css={ImageUploadContainerStyle}>
            {/* Desktop Image */}
            <div css={ImageUploadContainerStyle}>
              {!desktopImagePreview && imageUrls.desktop && <img
                src={imageUrls.desktop}
                alt="Podgląd zdjęcia na desktop"
                css={ImagePreviewStyle}
              />}
              {desktopImagePreview && <img src={desktopImagePreview} css={ImagePreviewStyle} />}
              {!desktopImagePreview && !imageUrls.desktop && <AddImageButton inputRef={desktopImageInputRef} onChange={handleDesktopImageChange} />}
            {!!imageUrls.desktop && <label css={FileInputLabelStyle} htmlFor="desktopImage">
              {'Zmień zdjęcie'}
            </label>}
              <input
                css={FileInputStyle}
                type="file"
                id="desktopImage"
                ref={desktopImageInputRef}
                accept="image/*"
                onChange={handleDesktopImageChange}
              />
              <span css={ImageCaptionStyle}>
                {desktopImageFile ? desktopImageFile.name : 'Wersja desktopowa'}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div css={ButtonContainerStyle}>
        <MyButton
          text="Anuluj"
          variant="outlined"
          colorVariant="primary"
          onClick={onCancel}
          disabled={isSaving}
        />
        <MyButton
          text={isSaving ? 'Zapisywanie...' : 'Zapisz zmiany'}
          variant="contained"
          colorVariant="primary"
          type="submit"
          disabled={isSaving}
          startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : undefined}
        />
      </div>
    </form>
  );
};

// @ts-ignore
const AddImageButton = ({inputRef, onChange}: {inputRef: RefObject, onChange: any}) => {
  return <div css={AddImageButtonStyle} onClick={() => inputRef.current?.click()}>
    <AddPhotoAlternateIcon color="primary" fontSize="large" />
    <span css={ImageCaptionStyle}>Dodaj zdjęcie</span>
    <input
      css={FileInputStyle}
      type="file"
      id="additionalImage"
      ref={inputRef}
      accept="image/*"
      onChange={onChange}
    />
  </div>
};